import useResizeObserver from "@react-hook/resize-observer";
import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { PlayArrow } from "@material-ui/icons";
import BugReportIcon from "@material-ui/icons/BugReport";
import { IS_DEV_ENV, IS_INTERNAL_ENV } from "constants/app.constants";
import {
  BUBBLE_MIN_ZOOM,
  FACILITY_LABEL_LAYER,
  SHAPEFILE_DETAILS_LABEL_LAYER,
  SHAPEFILE_NAME_LABELS_LAYER,
  STICK_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { RootState } from "store/rootReducer";
import {
  selectIsDashboardScreenshotActive,
  useDashboardScreenshotStore
} from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import {
  selectDisplaySettings,
  selectIsDebuggingMap,
  selectLayers,
  selectNetworkDepthLegend,
  selectTxnId,
  useMapStore,
  useUpdateLayers
} from "stores/map";
import type { MapLayer } from "stores/map";
import styled from "styled-components";

import { ErrorBoundary, ToolbarButton } from "components/base";
import HorizontalLoading from "components/base/HorizontalLoading";
import { useDashboardContext } from "components/dashboard/hooks";
import useShowExploitableInMap from "components/exploitable-report/hooks/useShowExploitableInMap";
import GeomBinDrawer from "components/geom-bin/GeomBinDrawer";
import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";
import AddPolygon from "components/geom-bin/toolbar/AddPolygon";
import DeletePolygon from "components/geom-bin/toolbar/DeletePolygon";
import TownshipRangeGrid from "components/map/TownshipRangeGrid";
import { SELF_MANAGED_LAYERS } from "components/map/hooks/manage-mapbox-layer-styles/useManageMapboxLayerStyles";
import { useProjectContext } from "components/project/projects/context";
import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";
import { useScreenshotContext } from "components/screenshot/hooks";
import { LegendPosition } from "components/shared/charts/legend/utils/getPositionFromChartSettings";
import Indicator from "components/ui/Indicator";
import { WellCardPreviewPopup } from "components/well-card-panel/WellCardPreviewPopup";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import useBetaFeatures from "../../hooks/useBetaFeatures";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import { EntityKind } from "../../models/entityKind";
import { MidstreamNavigatorProvider } from "../midstream/midstream-navigator/context/MidstreamNavigatorContext";
import GoToSyncWells from "../sync/GoToSyncWells";
import { FacilityHighlighter } from "./FacilityHighlighter";
import FacilityMapSeriesSizeConfirmation from "./FacilityMapSeriesSizeConfirmation";
import "./Map.scss";
import { MapAutomationHandler } from "./MapAutomationHandler";
import MapMessage from "./MapMessage";
import MapSeriesSizeConfirmation from "./MapSeriesSizeConfirmation";
import MapToolbar from "./MapToolbar";
import MapboxWrapper from "./MapboxWrapper";
import { MaxBinsToastNotifier } from "./MaxBinsToastNotifier";
import { NetworkVisualizationOverlay } from "./NetworkVisualizationOverlay";
import RigFracDetail from "./RigFracDetail";
import Timeline from "./Timeline";
import WellDetail from "./WellDetail";
import { IpdbStoreProvider } from "./contexts/IpdbStoreProvider";
import { MapProvider } from "./contexts/MapContext";
import { useMapRequestManager } from "./hooks/useMapRequestManager";
import useTypeLogWellsLayer from "./hooks/useTypeLogWellsLayer";
import { IpdbLegend } from "./legend/IpdbLegend";
import { MapScreenshotAutomationHandler } from "./screenshot/MapScreenshotAutomationHandler";
import Screenshot from "./screenshot/Screenshot";
import ScreenshotToggle from "./screenshot/ScreenshotToggle";
import LegendWrapper from "./screenshot/legend/LegendWrapper";
import { NetworkDepthLegendWrapper } from "./screenshot/legend/NetworkDepthLegendWrapper";
import {
  ShapefileLegendBase,
  useShapefileLegendItems
} from "./screenshot/legend/shapefile-legend";
import { getMapLayersFromSession } from "./session/map-layers/getMapLayersFromSession";
import {
  DEFAULT_IPDB_LABELS_SIZE,
  DEFAULT_IPDB_TITLE_SIZE
} from "./shared/ipdbLegendDefaults";
import BaseLayerToggle from "./toolbar/BaseLayerToggle";
import { BubbleVisLegend } from "./toolbar/BubbleVisLegend";
import GoToTownshipPlay from "./toolbar/GoToTownshipPlay";
import Ipdb from "./toolbar/Ipdb";
import LassoSelection from "./toolbar/LassoSelection";
import MapFullscreen from "./toolbar/MapFullscreen";
import MapLock from "./toolbar/MapLock";
import MapVis from "./toolbar/MapVis";
import XdaLineMarker from "./toolbar/XdaLineMarker";
import ZoomControl from "./toolbar/ZoomControl";
import ZoomToFilter from "./toolbar/ZoomToFilter";
import { MidstreamNavigatorToolbar } from "./toolbar/midstream-navigator/MidstreamNavigatorToolbar";
import { waitForStyleToLoad } from "./utils";
import { hasRenderedRigFracFeatures } from "./utils/hasRenderedRigFracFeatures";
import { setPaintProperty } from "./utils/layerOperations";
import { getFeaturesIntersectingPolygon } from "./utils/mapboxHelper";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function Map({ id, onFullscreen = null }): JSX.Element {
  //states
  const mapboxRef = useRef<mapboxgl.Map>(undefined);
  const [, setMapSync] = useState(true);
  const mapSyncRef = useRef(true);
  const drawRef = useRef(undefined);
  const portalRef = useRef<HTMLDivElement>(undefined);
  const { hasFeature } = useBetaFeatures();
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();
  const hasActiveDevelopmentTimeline = hasFeatureFlag("Active Development Timeline");

  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const showMidstreamNavigatorToolbar =
    hasFeatureFlag("Midstream Allocation Model") &&
    activeEntityKinds.includes(EntityKind.Facility);

  const [showTimeline, setShowTimeline] = useState(false);
  const [isTownshipRangeGridVisible, setIsTownshipRangeGridVisible] = useState(false);
  const [isBubbleLegendVisible, setIsBubbleLegendVisible] = useState(false);
  const [hasRigFracInView, setHasRigFracInView] = useState(false);
  const [screenshotOverlayVisible, setScreenshotOverlayVisible] = useState(false);

  const isDashboardScreenshotActive = useDashboardScreenshotStore(
    selectIsDashboardScreenshotActive
  );
  const { settings: screenshotSettings } = useScreenshotContext();
  // During dashboard screenshot, use individual settings for each legend
  const shouldHideIpdbLegend =
    isDashboardScreenshotActive &&
    screenshotSettings?.showIpdbLegendInScreenshot === false;
  // BubbleVisLegend shows upstream (Well) or midstream (Facility) depending on active entity
  const shouldHideBubbleLegend =
    isDashboardScreenshotActive &&
    (activeEntityKinds.includes(EntityKind.Facility)
      ? screenshotSettings?.showMidstreamLegendInScreenshot === false
      : screenshotSettings?.showLegendInScreenshot === false);
  const shouldShowShapefileLegend =
    isDashboardScreenshotActive &&
    screenshotSettings?.showShapefileLegendInScreenshot === true;
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);
  const shouldShowNetworkDepthLegend =
    isDashboardScreenshotActive &&
    screenshotSettings?.showNetworkDepthLegendInScreenshot !== false &&
    networkDepthLegend.legendItems.length > 0;
  const shouldShowUpstreamLegend =
    isDashboardScreenshotActive && screenshotSettings?.showLegendInScreenshot !== false;
  const shouldShowMidstreamLegend =
    isDashboardScreenshotActive &&
    screenshotSettings?.showMidstreamLegendInScreenshot !== false;

  const user = useSelector((state: RootState) => state.auth.user);
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  // Always holds the latest filterId. getAndSetFeaturesIntersectingPolygon is passed
  // as a prop to XdaLineMarker, which registers its draw handlers in effects that do
  // NOT list that prop in their deps — so those handlers keep whichever version of the
  // callback existed at mount, when filterId.id is still "". Reading through a ref
  // means the callback sees the current filter without needing a new identity.
  // @migration-note filterId + filterIdRef → useFilterStore when filterSlice migrates (Priority 1)
  const filterIdRef = useRef(filterId);
  // Kept in sync via effect rather than assigned during render. The
  // assignment is idempotent, so re-running it on every render would be
  // harmless but wasteful — scope the effect to [filterId] so it only runs
  // when filterId actually changes, not on every unrelated Map render
  // (resize, activity-bar drag, txnId, layers, etc.).
  useEffect(() => {
    filterIdRef.current = filterId;
  }, [filterId]);
  const { isActivityBarResizing } = useWorkspaceContext();
  const { checkedLayerKeys, selectedProjectId } = useProjectContext();
  const txnId = useMapStore(selectTxnId);
  const isDebuggingMap = useMapStore(selectIsDebuggingMap);
  const displaySettings = useMapStore(selectDisplaySettings);
  const polygonFilter = useSelector((state: RootState) => state.filter.polygonFilter);

  const shapefileFeatures = useSelector(
    (state: RootState) => state.project.shapefileFeatures
  );

  const selectionPolygonRef = useRef(null);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const mainToolbarRef = useRef<HTMLDivElement>(null);
  const [mainToolbarHeight, setMainToolbarHeight] = useState(50);

  const setSelectedWells = useMapStore((s) => s.setSelectedWells);
  const setViewLock = useMapStore((s) => s.setViewLock);
  const setIsDebuggingMap = useMapStore((s) => s.setIsDebuggingMap);
  const updateLayers = useUpdateLayers();

  //layers
  const layers = useMapStore(selectLayers) as MapLayer[];
  const layersRef = useRef(layers);
  const checkedLayerKeysRef = useRef(checkedLayerKeys);

  const { isActive: isGeomBinOpen } = useGeomBinContext();
  // A popped-out window renders real widgets, including this map, but the well
  // card panel is mounted only in the main window, so the click popup's View
  // Well Card would be a control with nothing to open.
  const { capabilities, isPopoutDashboard } = useDashboardContext();

  useShowExploitableInMap(mapboxRef.current);

  // Use the map request manager hook to handle all map request logic
  useMapRequestManager();

  const getAndSetFeaturesIntersectingPolygon = useCallback(
    async (polygon, filterToRenderedWells = true) => {
      if (mapboxRef.current && !mapboxRef.current.isStyleLoaded()) {
        // Wait for the map style to load before proceeding
        // otherwise we will risk interesting with empty map
        await waitForStyleToLoad(mapboxRef.current, 1000);
      }

      // Read through the ref, not the closed-over value — see filterIdRef above.
      const { selectedWells, wellsIntersect, selectedFeatures } =
        await getFeaturesIntersectingPolygon(
          mapboxRef.current,
          polygon,
          filterToRenderedWells,
          filterIdRef.current?.id
        );
      if (!selectedWells) {
        return [];
      }
      setSelectedWells(selectedWells);
      return [selectedWells, wellsIntersect, selectedFeatures];
    },
    // filterId deliberately omitted: it is read through filterIdRef so this callback
    // keeps a stable identity. Adding it here would churn the prop passed to
    // XdaLineMarker without reaching its already-registered handlers anyway.
    [setSelectedWells]
  );

  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }

    layersRef.current = [...layers];
  }, [layers]);

  useEffect(() => {
    checkedLayerKeysRef.current = checkedLayerKeys;
  }, [checkedLayerKeys]);

  const localState = useRef({
    selectionMode: "single",
    xdaLineMoved: false,
    isXdaLineMoving: false,
    currentMouseCoordinate: { lng: 0, lat: 0 },
    shapefilesLayers: [],
    shapefilesSources: [],
    addLayer: (layer, beforeLayer = null) => {
      const mapbox = mapboxRef.current;
      if (!mapbox) {
        return;
      }

      const layers = layersRef.current;

      try {
        mapbox.addLayer(layer, beforeLayer);

        // Check if the layer was actually added
        const addedLayer = mapbox.getLayer(layer.id);
        if (!addedLayer) {
          // The layer failed to be added, so abort
          Sentry.withScope((scope) => {
            scope.setContext("map_layer_addition", {
              layerId: layer.id,
              beforeLayer: beforeLayer,
              action: "addLayer",
              status: "failed"
            });
            scope.setLevel("error");
            Sentry.captureMessage(
              `[Map] Layer was not added to mapbox: ${layer.id}, beforeLayer was: ${beforeLayer}`
            );
          });
          return;
        }

        // Get the current Redux map layers to determine visibility
        const currentReduxLayers = layersRef.current;
        const existingReduxLayer = currentReduxLayers.find((l) => l.id === layer.id);

        // Priority for visibility determination:
        // 1. checkedLayerKeys (source of truth for shapefiles being actively toggled)
        // 2. Existing Redux state (for stable layers)
        // 3. Cached session data (for restoration after refresh)
        // 4. Layer's explicit Mapbox visibility (for newly added layers)
        // 5. Default to visible
        let isVisible = true; // Default to visible for new layers

        // For shapefile layers, avoid race condition with stale Redux state
        // Shapefiles are managed through a separate tree system, not checkedLayerKeys
        // Default to visible - useManageMapboxLayerStyles will handle final visibility
        // Shapefile layers are UUIDs (8-4-4-4-12 format) with optional suffixes
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(-stroke|-point|-property|-label)?$/i;
        const isShapefileLayer = uuidRegex.test(layer.id);

        if (isShapefileLayer) {
          if (layer.id.includes("-property")) {
            isVisible =
              checkedLayerKeysRef.current?.includes(SHAPEFILE_DETAILS_LABEL_LAYER) ??
              false;
          } else {
            // Always default to visible for shapefiles
            // This prevents race condition where Redux gets updated with false
            // before all rapid addLayer calls complete
            // useManageMapboxLayerStyles will set correct visibility after Redux stabilizes
            isVisible = true;
          }
        } else if (existingReduxLayer !== undefined) {
          // Use existing Redux state if available (user's current selection)
          isVisible = existingReduxLayer.isVisible;
        } else {
          // Fall back to cached session data for layer restoration
          const cachedLayers = getMapLayersFromSession(selectedProjectId);
          const cachedLayer = cachedLayers?.find((l) => l.id === layer.id);

          if (cachedLayer) {
            isVisible = cachedLayer.isVisible;
          } else {
            // Check layer's explicit visibility (e.g., shapetiles with layout.visibility)
            const mapboxVisibility = mapbox.getLayoutProperty(layer.id, "visibility");
            // If layer has explicit visibility set, use it; otherwise default to visible
            isVisible = mapboxVisibility === "none" ? false : true;
          }
        }

        // STICK_LAYER's own store entry is false by design — it mirrors the Wells
        // entry (see useManageMapboxLayerStyles) — so resolve it from there.
        const resolvedVisible =
          layer.id === STICK_LAYER
            ? currentReduxLayers.find((l) => l.id === WELL_LAYER)?.isVisible ?? true
            : isVisible;

        // The store write below (setLayers/updateLayers) that would otherwise hide
        // this layer is debounced, so on a style reload an unchecked layer would
        // paint for a frame or more before that debounce flushes. Apply the hide
        // synchronously here; never force-show, since some layers (see
        // SELF_MANAGED_LAYERS) manage their own visibility and default to hidden.
        // Skipped for SELF_MANAGED_LAYERS: calculateLayerVisibility returns false
        // for any id that isn't a layer-tree key, and nothing re-shows them, so a
        // hide here would be permanent (XDA projection).
        if (!resolvedVisible && !SELF_MANAGED_LAYERS.has(layer.id)) {
          mapbox.setLayoutProperty(layer.id, "visibility", "none");
        }

        const lineWidth =
          layer.id === WELL_LAYER ? mapbox.getPaintProperty(layer.id, "line-width") : 0;

        const circleRadius =
          layer.id === WELL_LAYER_POINT
            ? mapbox.getPaintProperty(WELL_LAYER_POINT, "circle-radius")?.[4]
            : 0;

        let textSize = 0;
        switch (layer.id) {
          case WELL_LABEL_LAYER:
            textSize = displaySettings.wellLabelSize;
            break;
          case FACILITY_LABEL_LAYER:
            textSize = displaySettings.facilityLabelSize;
            break;
          default:
            break;
        }

        const layerToAdd = {
          id: layer.id,
          isVisible: isVisible,
          minzoom: layer.minzoom,
          maxzoom: layer.maxzoom,
          lineWidth: lineWidth,
          circleRadius: circleRadius,
          textSize: textSize
        };

        const idx = layers.map((l) => l.id).indexOf(layerToAdd.id);
        // update existing layer
        if (idx >= 0) {
          layers[idx] = layerToAdd;
          setLayers([...layers]);
          return;
        }

        if (beforeLayer) {
          let addIndex = layers.length - 1;

          if (addIndex < 0) {
            addIndex = 0;
          }

          //find index of layer
          const idx = layers.map((l) => l.id).indexOf(beforeLayer);
          if (idx >= 0) {
            addIndex = idx;
          }

          layers.splice(addIndex, 0, layerToAdd);
          setLayers(layers);
          return;
        }

        // new layer, add it to the end
        layers.push(layerToAdd);
        setLayers(layers);
      } catch (error) {
        if (IS_DEV_ENV) {
          toast.error(`Failed to add layer ${layer.id}: ${error.message}`);
        } else {
          toast.error("Failed to add map layer. Please refresh if issues persist.");
        }
      }
    }
  });

  useTypeLogWellsLayer(
    mapboxRef.current,
    capabilities.hasTypeLogWidget,
    localState.current.addLayer
  );

  const setLayers = useCallback(
    (layers) => {
      const newLayers = [...layers];
      layersRef.current = layers;
      if (newLayers) {
        updateLayers(newLayers);
      }
    },
    [updateLayers]
  );

  useResizeObserver(containerRef, (entry) => {
    setContainerHeight(entry.contentRect.height);
    setContainerWidth(entry.contentRect.width);
  });

  useEffect(() => {
    if (mainToolbarRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setMainToolbarHeight(entry.contentRect.height + 20);
        }
      });

      resizeObserver.observe(mainToolbarRef.current);

      return () => {
        if (mainToolbarRef.current) {
          resizeObserver.unobserve(mainToolbarRef.current);
        }
        resizeObserver.disconnect();
      };
    }
  }, [mainToolbarRef.current]);

  useEffect(() => {
    return () => {
      mapboxRef.current = undefined;
      localState.current.shapefilesLayers = [];
      localState.current.shapefilesSources = [];
    };
  }, []);
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (mapbox != null && !isActivityBarResizing) {
      mapbox.resize();
    }
  }, [containerWidth, containerHeight, isActivityBarResizing]);

  const removeLayer = useCallback(
    (layerId) => {
      const mapbox = mapboxRef.current;
      if (!mapbox) {
        return;
      }

      if (mapbox.getLayer(layerId)) {
        mapbox.removeLayer(layerId);
      } else {
        return;
      }

      // Remove source if no other layers use it
      const layer = layersRef.current.find((l) => l.id === layerId);
      if (layer) {
        const sourceId = layer.sourceId; // Use the explicit sourceId property
        const layersUsingSource = mapbox
          .getStyle()
          .layers.filter((l) => "source" in l && l.source === sourceId);
        if (layersUsingSource.length === 0 && mapbox.getSource(sourceId)) {
          mapbox.removeSource(sourceId);
        }
      }

      // Update local layers state
      const updatedLayers = layersRef.current.filter((l) => l.id !== layerId);
      setLayers(updatedLayers);

      // Clear local references to shapefile layers & sources
      const selectedShapefileLayers = localState.current.shapefilesLayers;
      const index = selectedShapefileLayers.findIndex((l) => l.id === layerId);
      if (index >= 0) selectedShapefileLayers.splice(index, 1);

      const selectedShapefileSources = localState.current.shapefilesSources;
      const sourceIndex = selectedShapefileSources.findIndex((s) => s === layerId);
      if (sourceIndex >= 0) selectedShapefileSources.splice(sourceIndex, 1);
    },
    [setLayers]
  );

  // TODO BF: Move this out into a hook, just to manage the shapefile layers by checked geoms
  // Update mapbox when shapefile features visibility has been toggled
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (mapbox == null) return;
    for (const shapefileKey of Object.keys(shapefileFeatures)) {
      const currentLayerName = shapefileKey;

      let uncheckedFeatures = shapefileFeatures?.[shapefileKey]?.features
        .filter((f) => f.checked)
        .map((f) => f.shapefileGeomId);

      if (!uncheckedFeatures?.length) {
        uncheckedFeatures = [""];
      }

      const opacityExpression = [
        "match",
        ["get", "shapefileGeomId"],
        uncheckedFeatures,
        ["coalesce", ["feature-state", "opacity"], ["get", "opacity"], 1],
        0
      ];

      // Set fill opacity for main shapefile layer
      setPaintProperty(mapbox, currentLayerName, "fill-opacity", opacityExpression);

      // Set line opacity for stroke layer
      setPaintProperty(
        mapbox,
        `${currentLayerName}-stroke`,
        "line-opacity",
        opacityExpression
      );

      // Set circle opacity for point layer
      setPaintProperty(
        mapbox,
        `${currentLayerName}-point`,
        "circle-opacity",
        opacityExpression
      );
      setPaintProperty(
        mapbox,
        `${currentLayerName}-point`,
        "circle-stroke-opacity",
        opacityExpression
      );

      // Set text opacity for property labels
      const textOpacity = checkedLayerKeys.includes(SHAPEFILE_DETAILS_LABEL_LAYER)
        ? ["match", ["get", "shapefileGeomId"], uncheckedFeatures, 1, 0]
        : 0;

      const propertyLayerId = `${currentLayerName}-property`;
      setPaintProperty(mapbox, propertyLayerId, "text-opacity", textOpacity);

      // Set text opacity for name labels
      const nameLabelOpacity = checkedLayerKeys.includes(SHAPEFILE_NAME_LABELS_LAYER)
        ? ["match", ["get", "shapefileGeomId"], uncheckedFeatures, 1, 0]
        : 0;

      const nameLabelLayerId = `${currentLayerName}${shapefileLayerType.label}`;
      setPaintProperty(mapbox, nameLabelLayerId, "text-opacity", nameLabelOpacity);
    }
  }, [shapefileFeatures, checkedLayerKeys]);

  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }

    function onZoomEnd() {
      if (!mapbox) {
        return;
      }
      const zoom = mapbox.getZoom();
      setIsBubbleLegendVisible(zoom > BUBBLE_MIN_ZOOM);
    }

    mapbox.on("zoomend", onZoomEnd);
    return () => {
      if (mapbox) {
        mapbox.off("zoomend", onZoomEnd);
      }
    };
  }, [mapboxRef.current]);

  // "idle" fires once the view settles and its tiles finish rendering, so the
  // no-data indicator tracks what is actually on screen rather than layer toggles.
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }

    function onIdle() {
      if (!mapbox) {
        return;
      }
      setHasRigFracInView(hasRenderedRigFracFeatures(mapbox));
    }

    mapbox.on("idle", onIdle);
    return () => {
      if (mapbox) {
        mapbox.off("idle", onIdle);
      }
    };
  }, [mapboxRef.current]);

  function onSyncChanged(sync) {
    if (sync) {
      setViewLock(true);
    } else {
      if (polygonFilter && polygonFilter.source === "selection") {
        return;
      }
      setViewLock(false);
    }
  }

  //HorizontalLoading checks if the map is uninitialized, mapBusy is true, or shapefileQuery is fetching
  return (
    <div
      className={`Mapbox relative fill ${isTownshipRangeGridVisible ? "show-grid" : ""}`}
      data-testid="map-container"
      style={{ cursor: "context-menu" }}
      ref={containerRef}>
      <ErrorBoundary>
        <MapProvider>
          <IpdbStoreProvider>
            <Wrapper isInDrawingMode={isGeomBinOpen}>
              <MapSeriesSizeConfirmation />
              <FacilityMapSeriesSizeConfirmation />
              <MaxBinsToastNotifier />
              <HorizontalLoading />
              <TownshipRangeGrid onVisibleChange={setIsTownshipRangeGridVisible} />
              {hasActiveDevelopmentTimeline && showTimeline && (
                <Timeline
                  onClose={() => setShowTimeline(false)}
                  map={mapboxRef.current}
                />
              )}
              <FacilityHighlighter />
              {!isPopoutDashboard && <WellCardPreviewPopup />}
              <MidstreamNavigatorProvider>
                <NetworkVisualizationOverlay />
              </MidstreamNavigatorProvider>
              <MapboxWrapper
                addLayer={localState.current.addLayer}
                drawRef={drawRef}
                id={id}
                mapboxRef={mapboxRef}
                removeLayer={removeLayer}
                setLayers={setLayers}
              />

              <Indicator
                map={true}
                hasRigFracInView={hasRigFracInView}
                message={
                  hasFeature("Rig And Frac Layers")
                    ? "No Wells, Facilities, Rigs, or Frac Crews Available"
                    : "No Wells or Facilities Available"
                }
              />
              <ZoomControl />
              <Screenshot
                screenshotOverlayVisible={screenshotOverlayVisible}
                setScreenshotOverlayVisible={setScreenshotOverlayVisible}
              />
              <MapScreenshotAutomationHandler
                setScreenshotOverlayVisible={setScreenshotOverlayVisible}
              />
              <div className="navigate-control">
                {/* Stays visible in geom-bin mode on purpose (like MapFullscreen below) — zooming to the filter is still useful while binning. */}
                <ZoomToFilter />
                {!isGeomBinOpen && <GoToTownshipPlay />}
                {!isGeomBinOpen && (
                  <ScreenshotToggle
                    screenshotOverlayVisible={screenshotOverlayVisible}
                    setScreenshotOverlayVisible={setScreenshotOverlayVisible}
                  />
                )}
                <MapFullscreen onFullscreen={onFullscreen} />
              </div>
              <MapToolbar ref={mainToolbarRef} className="absolute pin-topleft">
                <AddPolygon portalRef={portalRef} drawRef={drawRef} />
                <DeletePolygon portalRef={portalRef} drawRef={drawRef} />
                <MapLock
                  onMapLockChanged={(val) => {
                    const mapbox = mapboxRef.current;
                    if (mapbox) {
                      // Only sync the map if the view is not locked
                      mapSyncRef.current = !val;
                      setMapSync(mapSyncRef.current);
                    }
                  }}
                  getAndSetFeaturesIntersectingPolygon={
                    getAndSetFeaturesIntersectingPolygon
                  }
                  mapSyncRef={mapSyncRef}
                  setMapSync={setMapSync}
                />
                <LassoSelection portalRef={portalRef} drawRef={drawRef} />
                <GeomBinDrawer portalRef={portalRef} drawRef={drawRef} />
                <XdaLineMarker
                  localState={localState}
                  getAndSetFeaturesIntersectingPolygon={
                    getAndSetFeaturesIntersectingPolygon
                  }
                  drawRef={drawRef}
                  selectionPolygonRef={selectionPolygonRef}
                />
                <MapVis
                  addLayer={localState.current.addLayer}
                  removeLayer={removeLayer}
                  mapbox={mapboxRef.current}
                  filterId={filterId}
                  txnId={txnId}
                />
                {!isGeomBinOpen && (
                  <>
                    <Ipdb
                      addLayer={localState.current.addLayer}
                      removeLayer={removeLayer}
                    />
                    {hasActiveDevelopmentTimeline && (
                      <ToolbarButton
                        active={showTimeline}
                        icon={<PlayArrow fontSize="large"></PlayArrow>}
                        tooltipText="Development Timeline"
                        onToggle={setShowTimeline}
                        overflowLabel={""}
                        styleKey="mapToolbar"
                      />
                    )}
                  </>
                )}
                {(IS_DEV_ENV || user?.isImpersonating) && (
                  <ToolbarButton
                    active={isDebuggingMap}
                    icon={<BugReportIcon fontSize="large" />}
                    tooltipText="Debug Map Hooks"
                    onToggle={(value) => setIsDebuggingMap(value)}
                    overflowLabel={""}
                    styleKey="mapToolbar"
                  />
                )}
              </MapToolbar>
              {showMidstreamNavigatorToolbar && (
                <MidstreamNavigatorToolbar topOffset={mainToolbarHeight + 30} />
              )}
              <WellDetail />
              <RigFracDetail />

              {!shouldHideIpdbLegend && (
                <IpdbLegend
                  location="map"
                  parentDimensions={
                    containerWidth && containerHeight
                      ? { width: containerWidth, height: containerHeight }
                      : null
                  }
                  {...(isDashboardScreenshotActive
                    ? {
                        ipdbTitleSize: Number(
                          screenshotSettings?.legendTitleSize ?? DEFAULT_IPDB_TITLE_SIZE
                        ),
                        ipdbFontSize: Number(
                          screenshotSettings?.legendFontSize ?? DEFAULT_IPDB_LABELS_SIZE
                        ),
                        backgroundOpacity: Number(
                          screenshotSettings?.ipdbLegendOpacity ?? 1
                        ),
                        showLegendBorder: Boolean(
                          screenshotSettings?.showIpdbLegendBorderInScreenshot ?? true
                        )
                      }
                    : {})}
                />
              )}
              {IS_INTERNAL_ENV && (
                <GoToSyncWells mapbox={mapboxRef.current} onSync={onSyncChanged} />
              )}
              <BaseLayerToggle />

              <MapMessage />
              {isBubbleLegendVisible && !shouldHideBubbleLegend && (
                <BubbleVisLegend
                  parentDimensions={{ width: containerWidth, height: containerHeight }}
                  screenshotLegend={false}
                />
              )}
              {shouldShowUpstreamLegend && (
                <LegendWrapper
                  width={250}
                  parentDimensions={{ width: containerWidth, height: containerHeight }}
                  isScreenshotOverlayVisible={true}
                  entityKind={EntityKind.Well}
                  defaultPosition={LegendPosition.NE}
                />
              )}
              {shouldShowMidstreamLegend && (
                <LegendWrapper
                  width={250}
                  parentDimensions={{ width: containerWidth, height: containerHeight }}
                  isScreenshotOverlayVisible={true}
                  entityKind={EntityKind.Facility}
                  defaultPosition={LegendPosition.NW}
                />
              )}
              {shouldShowShapefileLegend && (
                <DashboardShapefileLegend
                  parentDimensions={{ width: containerWidth, height: containerHeight }}
                  settings={screenshotSettings}
                />
              )}
              {shouldShowNetworkDepthLegend && (
                <NetworkDepthLegendWrapper isScreenshotOverlayVisible={true} />
              )}
              <MapAutomationHandler />
              <div ref={portalRef} />
            </Wrapper>
          </IpdbStoreProvider>
        </MapProvider>
      </ErrorBoundary>
    </div>
  );
}

const DEFAULT_SHAPEFILE_TITLE_SIZE = 15;
const DEFAULT_SHAPEFILE_LABELS_SIZE = 14;

const DashboardShapefileLegend: React.FC<{
  parentDimensions: { width: number; height: number };
  settings: Record<string, unknown>;
}> = ({ parentDimensions, settings }) => {
  const showDetails = Boolean(settings?.shapefileLegendShowDetails);
  const legendData = useShapefileLegendItems(showDetails);

  // In dashboard mode, use the global legend font sizes (legendTitleSize / legendFontSize)
  // so that the shapefile legend stays in sync with upstream/midstream legends.
  const titleSize = Number(settings?.legendTitleSize ?? DEFAULT_SHAPEFILE_TITLE_SIZE);
  const labelsSize = Number(settings?.legendFontSize ?? DEFAULT_SHAPEFILE_LABELS_SIZE);

  return (
    <ShapefileLegendBase
      legendData={legendData}
      showLegend={true}
      showBorder={Boolean(settings?.showShapefileLegendBorderInScreenshot ?? true)}
      titleSize={titleSize}
      labelsSize={labelsSize}
      backgroundOpacity={Number(settings?.shapefileLegendOpacity ?? 1)}
      isWrappingText={Boolean(settings?.isWrappingTextShapefile ?? true)}
      parentDimensions={parentDimensions}
      mapScreenshotScale={1}
      isScreenshotOverlayVisible={false}
    />
  );
};

const Wrapper = styled.div`
  padding-top: 30px;
  padding-left: 35px;
  position: relative;
  height: 100%;

  .mapboxgl-ctrl-ruler {
    display: ${(props) => (props.isInDrawingMode ? "none" : "block")};
  }
`;
